/**
 * Offline indicator: the page's one connectivity and sync status bar.
 *
 * Offline (amber) it says the page is a saved copy, or how many offline changes
 * are waiting; online (blue) it shows PowerSync's first download and uploads
 * in progress. There is no second sync banner: every state lands in this bar,
 * under the page banner, clear of the notch.
 *
 * The service worker serves pages cache-first, then revalidates in the background.
 * When that revalidation fetch FAILS, the page we painted is stale and we are
 * offline, so sw.js posts { type: 'CONNECTIVITY', online: false }. When a navigation
 * fetch succeeds it posts { online: true }. This module shows/hides the slim
 * #offline-indicator bar accordingly.
 *
 * We key off the service worker's fetch outcome, not navigator.onLine: navigator.onLine
 * only knows whether the device has a network, not whether the server is reachable
 * (wifi with no internet, a captive portal, or the server being down all read as
 * "online"). A failed fetch is the accurate "you're seeing a saved copy" signal.
 *
 * COLD START: that signal cannot reach a document that did not exist yet. On a
 * hard offline load the SW posts CONNECTIVITY while fetching, before this
 * listener is bound (and to a resultingClientId that may not resolve), so the
 * bar never appeared and — because the recovery probe only runs once the page
 * knows it is offline — the network returning never refreshed it either: stale
 * figures presented as live. navigator.onLine === false is a TRUE negative
 * (the device knows it has no network), so it is trusted at startup and on the
 * window offline event to seed that state. The subtler cases it cannot see
 * (reachable network, unreachable server) still arrive via the SW.
 *
 * RECOVERY: a page painted from cache is a stale snapshot, and nothing else will
 * ever update it — the SW only signals on navigation fetches, so a parked page
 * hears nothing when the network returns. While the indicator is up we probe
 * /api/version (the endpoint the SW passes through untouched) on the online
 * event and on a slow poll; the first probe that answers asks the page check
 * (window.chippiePageCheck.refresh, static/js/page-check.js) whether the page is
 * current. It swaps only when the server's page differs, and waits while the
 * user is typing, so text entered offline survives the reconnect. Re-visiting
 * the URL instead would repaint the same saved copy from the worker's cache and
 * replace the body under the user. A successful real navigation needs no
 * refresh — the page it painted IS fresh.
 *
 * Paired with: static/sw.js navigationHandler. CSS: static/css/offline-indicator.css
 */
(function () {
  if (!('serviceWorker' in navigator)) return;
  if (window.__offlineIndicatorInit) return;   // register the listener once (script may re-run on Turbo nav)
  window.__offlineIndicatorInit = true;

  var wasOffline = false;
  var pollTimer = null;
  // Last PowerSync state announced by powersync/database.js (which owns the
  // facts). Stays empty where PowerSync is not loaded.
  var sync = { pendingCount: 0, uploading: false, initialDownload: false, downloaded: null, total: null };

  function changes(n) {
    return n + ' change' + (n === 1 ? '' : 's');
  }

  // What the bar says, or null when it has nothing to say. Offline wins over
  // every sync state: offline, PowerSync only retries an upload that cannot
  // reach the server, so "Uploading" would be false.
  function barMessage() {
    if (wasOffline) {
      return {
        syncing: false,
        text: 'Offline · ' + (sync.pendingCount > 0 ? changes(sync.pendingCount) + ' waiting' : 'showing your saved version'),
      };
    }
    if (sync.initialDownload) {
      return {
        syncing: true,
        text: sync.total ? 'Downloading your data… ' + sync.downloaded + '/' + sync.total + ' records' : 'Downloading your data…',
      };
    }
    if (sync.uploading && sync.pendingCount > 0) {
      return { syncing: true, text: 'Uploading ' + changes(sync.pendingCount) + '…' };
    }
    return null;
  }

  // The bar sits directly under the page's banner. A detail page's header zone
  // holds more than the banner (the actions card follows it), so the bar is
  // moved to right after the banner rather than after the whole zone. Re-run
  // on every render: the skeleton swap and the body swap both rebuild the zone.
  // demo-capture.css expects the hidden bar in this slot when it keeps the demo
  // banner flush under the header; moving the bar elsewhere means updating it.
  function render() {
    var bar = document.getElementById('offline-indicator');
    if (!bar) return;
    var banner = document.querySelector('#page-header-zone .dashboard-header');
    if (banner && banner.nextElementSibling !== bar) banner.after(bar);
    var message = barMessage();
    if (message) bar.querySelector('.offline-indicator-text').textContent = message.text;
    bar.classList.toggle('is-syncing', !!(message && message.syncing));
    bar.classList.toggle('is-visible', !!message);
  }

  document.addEventListener('turbo:render', render);
  document.addEventListener('turbo:load', render);
  // skeleton-nav.js keeps the bar alive through its swap; place it under the
  // skeleton's banner so it stays put until the real page arrives.
  document.addEventListener('skeleton:shown', render);
  document.addEventListener('powersync:sync-state', function (event) {
    sync = event.detail;
    render();
  });

  function markOnline(refresh) {
    wasOffline = false;
    render();
    if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
    if (refresh) window.chippiePageCheck.refresh();
  }

  function markOffline() {
    wasOffline = true;
    render();
    if (!pollTimer) pollTimer = setInterval(probe, 5000);
  }

  function probe() {
    if (!wasOffline) return;
    fetch('/api/version', { cache: 'no-store', credentials: 'same-origin' })
      .then(function (r) { if (r.ok && wasOffline) markOnline(true); })
      .catch(function () { /* still unreachable — indicator stays up */ });
  }

  navigator.serviceWorker.addEventListener('message', function (event) {
    if (!event.data || event.data.type !== 'CONNECTIVITY') return;
    if (event.data.online === false) markOffline();
    else markOnline(false);   // signal came from a successful navigation — already fresh
  });

  window.addEventListener('online', probe);
  // A device-level drop is a true negative: mark it without waiting for a
  // navigation to fail. Seeds wasOffline so the online-event probe can fire.
  window.addEventListener('offline', markOffline);
  if (navigator.onLine === false) markOffline();
})();
